import { useEffect, useState } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useClub } from '../App';
import { toast } from 'sonner';
import api from '../utils/api';
import { Users, CheckCircle2, XCircle, Loader2 } from 'lucide-react';

export default function JoinClubPage() {
  const { code } = useParams();
  const { user } = useAuth();
  const { selectClub } = useClub();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');
  const [clubId, setClubId] = useState(null);

  useEffect(() => {
    if (!user || !code) return;
    api.post(`/clubs/join/${code}`)
      .then(res => {
        setStatus('success');
        setMessage(res.data.message);
        setClubId(res.data.club_id);
        toast.success(res.data.message);
      })
      .catch(err => {
        setStatus('error');
        setMessage(err.response?.data?.detail || 'Failed to join club');
        toast.error(err.response?.data?.detail || 'Failed to join club');
      });
  }, [user, code]);

  if (!user) return <Navigate to={`/login?redirect=${encodeURIComponent(`/join/${code}`)}`} replace />;

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="join-club-page">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 max-w-md w-full text-center">
        <div className={`w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center ${
          status === 'loading' ? 'bg-slate-100' : status === 'success' ? 'bg-green-100' : 'bg-red-100'
        }`}>
          {status === 'loading' && <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />}
          {status === 'success' && <CheckCircle2 className="w-8 h-8 text-green-600" />}
          {status === 'error' && <XCircle className="w-8 h-8 text-red-500" />}
        </div>
        <h2 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {status === 'loading' ? 'Joining Club...' : status === 'success' ? 'Joined!' : 'Oops'}
        </h2>
        <p className="text-sm text-slate-600 mb-6">{message || 'Processing your invite...'}</p>
        {status !== 'loading' && (
          <button
            onClick={() => {
              if (clubId) {
                api.get('/clubs').then(res => {
                  const club = res.data.find(c => c.id === clubId);
                  if (club) selectClub(club);
                  navigate('/dashboard');
                }).catch(() => navigate('/clubs'));
              } else {
                navigate('/clubs');
              }
            }}
            className="bg-green-600 hover:bg-green-700 text-white px-6 py-2.5 rounded-lg text-sm font-medium transition-all"
            data-testid="go-to-club-btn"
          >
            {status === 'success' ? 'Go to Club' : 'Go to My Clubs'}
          </button>
        )}
      </div>
    </div>
  );
}
